import {
	faCheck,
	faPencil,
	faPlus,
	faTrash,
	faUpload
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
	Button,
	Column,
	Columns,
	Dropdown,
	DropdownButton,
	FormControl,
	PaginatedTable,
	Select
} from 'vio-components';
import {
	capitalize,
	convertUTCStringToLocalDate,
	sortAlphabetically,
	sortByBooleanValue,
	sortByDateValue
} from 'vio-libs';

import {
	IMapFeatureLayer,
	IMapFeatureList,
	IMapFeatureRow
} from '../interfaces';
import { getSourceTableRows } from '../lib';

const SourcesList: React.FC<IMapFeatureList> = ({
	isLoading,
	layers,
	sources
}) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [ data, setData ] = useState<Array<IMapFeatureRow>>([]);
	const [ fiberNameFilter, setFiberNameFilter ] = useState('');
	const [ fiberBackboneFilter, setFiberBackboneFilter ] = useState<number>(0);

	const handleAdd = () => {
		navigate('/assets/add');
	};

	const handleEdit = (id: IMapFeatureRow['id']) => {
		navigate(`/assets/${id}/edit`, { state: data.find((fiber: IMapFeatureRow) => (id === fiber.id)) });
	};

	const handleDelete = (id: IMapFeatureRow['id']) => {
		navigate(`/assets/${id}/delete`, { state: data.find((fiber: IMapFeatureRow) => (id === fiber.id)) });
	};

	const handleUpload = (id: IMapFeatureRow['id']) => {
		navigate(`/assets/${id}/upload`, { state: data.find((fiber: IMapFeatureRow) => (id === fiber.id)) });
	};

	useEffect(() => {
		// build rows from sources and layers
		if (layers && sources && sources.length > 0 && layers.length > 0) {
			setData(getSourceTableRows(sources, layers).filter((row: IMapFeatureRow) => {
				let filteredRow = row;
				const filteredRowName = filteredRow.name.toLowerCase();

				if (fiberNameFilter.length > 0) {
					filteredRow = filteredRowName?.indexOf(fiberNameFilter.toLowerCase()) > -1 ? filteredRow : null;
				}

				if (fiberBackboneFilter) {
					filteredRow = filteredRow?.backbone ? filteredRow : null;
				}

				return filteredRow;

			}));
		}
	}, [ sources, layers, fiberNameFilter, fiberBackboneFilter ]);

	const renderFilters = (): JSX.Element => {
		return (
			<>
				<td data-cy='fiberNameFilter'>
					<FormControl
						placeholder='Filter fiber name'
						onChange={ (evt: React.ChangeEvent<HTMLInputElement>) => {
							const val = evt.target?.value;

							setFiberNameFilter(val);
						} }
						type='text'
						value={ fiberNameFilter } />
				</td>
				<td />
				<td width={ 180 } data-cy='fiberBackboneFilter'>
					<Select
						className='select-form-control'
						onChange={ (val) => {
							setFiberBackboneFilter(val);
						} }
						options={ [ {
							label: t('YES'),
							value: 1
						} ] }
						placeholder='Filter backbones'
						value={ fiberBackboneFilter } />
				</td>
			</>
		);
	};

	return (
		<PaginatedTable
			data={ data }
			filtersRenderer={ renderFilters() }
			pageSize={ 25 }
			requestingTableData={ isLoading }>
			<Columns>
				<Column
					field='name'
					sortable
					sortLogic={ sortAlphabetically }
					sortDir='asc'>
					Name
				</Column>

				<Column
					field='future'
					className='text-center'
					renderer={ (value: boolean) => (value ? <FontAwesomeIcon icon={ faCheck } /> : '') }
					sortLogic={ sortByBooleanValue }
					sortable
					sortDir='desc'>
					Future
				</Column>

				<Column
					field='backbone'
					className='text-center'
					renderer={ (value: boolean) => (value ? <FontAwesomeIcon icon={ faCheck } /> : '') }
					sortLogic={ sortByBooleanValue }
					sortable
					sortDir='desc'>
					Backbone
				</Column>

				<Column
					field='layer'
					renderer={ (value: IMapFeatureLayer) => (value ? <FontAwesomeIcon icon={ faCheck } /> : '') }>
					Layer
				</Column>

				<Column field='type'
					renderer={ (val: string) => capitalize(val) }
					sortable
					sortLogic={ sortAlphabetically }
					sortDir='desc'>
					Type
				</Column>

				<Column field='createdDate'
					defaultSortedColumn
					renderer={ (value: string) => convertUTCStringToLocalDate(value, true) }
					sortable
					sortLogic={ sortByDateValue }
					sortDir='desc'>
					Created Date
				</Column>

				<Column field='updatedDate'
					renderer={ (value: string) => convertUTCStringToLocalDate(value, true) }
					sortable
					sortLogic={ sortByDateValue }
					sortDir='desc'>
					Updated Date
				</Column>

				<Column
					className='d-flex flex-column align-items-end'
					field='id'
					renderer={
						(val: IMapFeatureRow['id'], row) => (
							<div className='d-flex flex-row justify-content-between'>
								<DropdownButton title='Actions' id='actions' >
									<Dropdown.Item onClick={ () => handleEdit(val) }>
										<FontAwesomeIcon icon={ faPencil } /> Edit Asset
									</Dropdown.Item>

									<Dropdown.Item onClick={ () => handleUpload(val) } disabled={ !row.layer }>
										<FontAwesomeIcon icon={ faUpload } /> Upload Fiber
									</Dropdown.Item>

									<Dropdown.Item onClick={ () => handleDelete(val) }>
										<FontAwesomeIcon icon={ faTrash } /> Delete Asset
									</Dropdown.Item>
								</DropdownButton>
							</div>)
					}>
					<Button
						className='add'
						onClick={ handleAdd }
						size='sm'
						variant='primary'>
						<FontAwesomeIcon icon={ faPlus } aria-hidden/> Add
					</Button>
				</Column>
			</Columns>
		</PaginatedTable>
	);
};

export default SourcesList;
